import { NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomTranslateLoader } from '@core/i18n/custom-translate-loader';
import { mapToSupportedLocale } from '@core/i18n/locale-mapper';
import { ConfigService } from '@core/services/config.service';
import { DeviceService } from '@core/services/device.service';
import { PreviewService } from '@core/services/preview.service';
import { PAGE_TITLES, usePageTitle } from '@core/utils/page-title.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BREAKPOINTS, DEVICE_TYPES } from '@shared/constants/breakpoints';
import { AvailablePage } from '@shared/models/components/event-pages-simple';
import { QuestionSimple } from '@shared/models/components/event-question-simple';
import {
  PreviewMode,
  toPreviewMode,
} from '@shared/models/enums/preview-mode.enum';
import { Language } from '@shared/models/language';
import {
  DEFAULT_PREVIEW_STATE,
  PreviewState,
} from '@shared/models/preview/preview-state';
import { fromEvent } from 'rxjs';
import { v7 as uuidV7 } from 'uuid';
import { PreviewHeaderComponent } from '../preview-header/preview-header.component';
import { PreviewSidebarComponent } from '../preview-sidebar/preview-sidebar.component';
import { PreviewTopbarComponent } from '../preview-topbar/preview-topbar.component';
enum ComponentType {
  CHOOSE_LANGUAGE = 'CHOOSE_LANGUAGE',
  QUESTION_TITLE = 'QUESTION_TITLE',
  PAGES_SIMPLE = 'PAGE_TITLE',
}

@Component({
  selector: 'app-preview-page',
  imports: [
    PreviewSidebarComponent,
    PreviewTopbarComponent,
    PreviewHeaderComponent,
    TranslateModule,
    NgClass,
  ],
  templateUrl: './preview-page.component.html',
  styleUrl: './preview-page.component.scss',
})
class PreviewPageComponent implements OnInit {
  @ViewChild('previewiframe') previewIframe!: ElementRef<HTMLIFrameElement>;
  @ViewChild('printiframe') printIframe!: ElementRef<HTMLIFrameElement>;
  @ViewChild(PreviewSidebarComponent) sidebar!: PreviewSidebarComponent;
  private readonly TRANSLATION_KEYS = ['preview.', 'question.'];
  iframeUrl?: SafeResourceUrl | null;
  languages: Language[] = [];
  questions: QuestionSimple[] = [];
  state: PreviewState = DEFAULT_PREVIEW_STATE;
  questId: string | null = '';
  previewLanguage = '';
  fallbackLanguage?: string;
  availablePages: AvailablePage[] = [];
  private setPageTitle = usePageTitle();
  isMobileMenuOpen = false;
  forceMobileLayout: boolean;

  private readonly isMobileOrTablet: boolean;
  private portraitMediaQuery = window.matchMedia('(orientation: portrait)');

  constructor(
    private route: ActivatedRoute,
    private configService: ConfigService,
    private sanitizer: DomSanitizer,
    private previewService: PreviewService,
    private translateLoader: CustomTranslateLoader,
    private translateService: TranslateService,
    private router: Router,
    private destroyRef: DestroyRef,
    private deviceService: DeviceService,
  ) {
    this.isMobileOrTablet = this.deviceService.isMobileOrTablet;
    this.forceMobileLayout =
      this.isMobileOrTablet && this.portraitMediaQuery.matches;

    fromEvent<MediaQueryListEvent>(this.portraitMediaQuery, 'change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((e) => {
        this.updateForceMobileLayout(e.matches);
      });
  }

  private updateForceMobileLayout(isPortrait: boolean): void {
    this.forceMobileLayout = this.isMobileOrTablet && isPortrait;
    if (!this.forceMobileLayout) {
      this.isMobileMenuOpen = false;
    }
    this.setMobileDeviceView();
  }

  ngOnInit(): void {
    this.setPageTitle(PAGE_TITLES.PREVIEW.key, PAGE_TITLES.PREVIEW.fallback);
    this.initializePreview();
    this.setMobileDeviceView();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setMobileDeviceView();
  }

  private setMobileDeviceView(): void {
    // On real mobile/tablet devices, keep desktop-view for the iframe
    // (only the topbar/sidebar layout is forced to mobile)
    if (this.forceMobileLayout) {
      return;
    }
    const isMobile = window.innerWidth < BREAKPOINTS.MD;
    const targetDevice = isMobile ? DEVICE_TYPES.MOBILE : DEVICE_TYPES.DESKTOP;
    // Only update if device type actually changed
    if (this.sidebar && this.sidebar.selectedDevice !== targetDevice) {
      this.handleDeviceChange(targetDevice);
    } else if (!this.sidebar) {
      // First load before sidebar is available
      this.handleDeviceChange(targetDevice);
    }
  }

  onIframeLoad(event: Event) {
    const iframe = event.target as HTMLIFrameElement;
    iframe.contentWindow?.addEventListener('click', () => {
      window.postMessage('iframe-click', '*');
    });
  }

  private async initializePreview(): Promise<void> {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (params) => {
        this.questId = params.get('questId');
      });
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (queryParams) => {
        this.state.mode = toPreviewMode(
          queryParams.get('mode'),
          DEFAULT_PREVIEW_STATE.mode,
        );
        this.state.rulesInText =
          queryParams.get('showConditions') ||
          DEFAULT_PREVIEW_STATE.rulesInText;
        const questionId = queryParams.get('questionId');
        if (questionId) {
          this.state.selectedQuestion = questionId;
        }
        this.state.sessionId = queryParams.get('sessionId') || uuidV7();
        const languageParam = queryParams.get('language');
        this.previewLanguage = mapToSupportedLocale(
          languageParam || navigator.language,
        );
        await this.updatePreview();
        await this.updateContentPrint();
      });
  }

  private async updatePreview(): Promise<void> {
    try {
      if (this.questId) {
        this.getMeta(this.questId);
      } else if (this.previewLanguage) {
        await this.setTranslations();
      }

      const params = this.previewService.createUrlParams(this.state);
      const baseUrl =
        this.configService.get('CLIENT_URL') +
        '/preview-content/' +
        this.questId;
      const sanitizedUrl = this.previewService.validateAndSanitizeUrl(
        baseUrl,
        params,
      );
      this.iframeUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(sanitizedUrl);
    } catch (error) {
      console.error('Error updating preview:', error);
    }
  }

  handleDeviceChange(deviceType: string): void {
    const iframe = document.querySelector('iframe');
    if (!iframe) return;

    iframe.classList.remove('mobile-view', 'tablet-view', 'desktop-view');

    switch (deviceType.toLowerCase()) {
      case DEVICE_TYPES.MOBILE:
        iframe.classList.add('mobile-view');
        break;
      case DEVICE_TYPES.TABLET:
        iframe.classList.add('tablet-view');
        break;
      case DEVICE_TYPES.DESKTOP:
        iframe.classList.add('desktop-view');
        break;
    }

    // Sync sidebar dropdown state
    if (this.sidebar) {
      this.sidebar.selectedDevice = deviceType;
    }
  }

  async handleGoToChange(value: {
    type: 'question' | 'page';
    key: string;
  }): Promise<void> {
    this.state.selectedPage = '';
    this.state.selectedQuestion = '';
    if (value.type === 'question') {
      this.state.selectedQuestion = value.key;
    } else if (value.type === 'page') {
      this.state.selectedPage = value.key;
    }
    await this.redirectWithUpdatedParams();
  }

  async handleLanguageChange(languageCode: string): Promise<void> {
    this.state.selectedLanguage = languageCode;
    if (this.questId) {
      this.getMeta(this.questId, languageCode, { fullFetch: false });
    }
    await this.redirectWithUpdatedParams();
  }

  async handleRulesInTextChange(showRulesInText: string): Promise<void> {
    this.state.rulesInText = showRulesInText;
    await this.redirectWithUpdatedParams();
  }

  async handlePreviewModeChange(previewMode: PreviewMode): Promise<void> {
    if (this.state.mode !== previewMode) {
      this.state.mode = previewMode;
      await this.redirectWithUpdatedParams();
    }
  }

  //This method should be changed when previewMode is working properly.
  handleUseRulesChange(useRules: string) {
    if (useRules === 'true') {
      this.state.mode = PreviewMode.SimulateResponse;
    } else if (useRules === 'false') {
      this.state.mode = PreviewMode.FreeFlow;
    }
    this.state.evalRules = useRules;
    this.redirectWithUpdatedParams();
  }

  handleMenuToggle(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  private async redirectWithUpdatedParams(): Promise<void> {
    // this.syncRouteWithState();
    const params = this.previewService.createUrlParams(this.state);
    this.updateContent(params);
  }

  private syncRouteWithState(): void {
    const queryParams = {
      mode: this.state.mode,
      showConditions: String(this.state.rulesInText),
      // evalRules: String(this.state.evalRules),
      language: this.state.selectedLanguage || null,
      // questionId: this.state.selectedQuestion || null,
      // pageName: this.state.selectedPage || null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: undefined,
      replaceUrl: true,
    });
  }

  private updateContent(params: URLSearchParams): void {
    const baseUrl =
      this.configService.get('CLIENT_URL') + '/preview-content/' + this.questId;
    const sanitizedUrl = this.previewService.validateAndSanitizeUrl(
      baseUrl,
      params,
    );
    this.iframeUrl =
      this.sanitizer.bypassSecurityTrustResourceUrl(sanitizedUrl);
    this.updateContentPrint(params);
  }

  private async updateContentPrint(params?: URLSearchParams): Promise<void> {
    if (!params) {
      params = this.previewService.createUrlParams(this.state);
    }
    const baseUrl =
      this.configService.get('CLIENT_URL') + '/print/' + this.questId;
    const sanitizedUrl = this.previewService.validateAndSanitizeUrl(
      baseUrl,
      params,
    );
    this.printIframe.nativeElement.src = sanitizedUrl;
  }

  triggerPrint(): void {
    const printIframe = this.printIframe.nativeElement;
    const iframeWindow = printIframe.contentWindow;

    if (!iframeWindow) {
      return;
    }

    const print = () => {
      this.syncPrintIframeWithPreview();
      iframeWindow.focus();
      iframeWindow.print();
    };

    if (printIframe.contentDocument?.readyState === 'complete') {
      print();
    } else {
      printIframe.addEventListener('load', print, { once: true });
    }
  }

  private syncPrintIframeWithPreview(): void {
    const previewDocument = this.previewIframe?.nativeElement.contentDocument;
    const printDocument = this.printIframe?.nativeElement.contentDocument;

    if (!previewDocument || !printDocument) {
      return;
    }

    this.syncCheckedInputs(previewDocument, printDocument);
    this.syncTextFields(previewDocument, printDocument);
  }

  private syncCheckedInputs(
    previewDocument: Document,
    printDocument: Document,
  ): void {
    const selector = 'input[type="radio"], input[type="checkbox"]';
    const previewInputs = Array.from(
      previewDocument.querySelectorAll<HTMLInputElement>(selector),
    );
    const printInputs = Array.from(
      printDocument.querySelectorAll<HTMLInputElement>(selector),
    );

    printInputs.forEach((printInput) => {
      printInput.checked = false;
      printInput.removeAttribute('checked');
    });

    previewInputs.forEach((previewInput) => {
      const printInput = this.findMatchingInput(previewInput, printInputs);

      if (!printInput) {
        return;
      }

      printInput.checked = previewInput.checked;
      if (previewInput.checked) {
        printInput.setAttribute('checked', 'checked');
      }
    });
  }

  private syncTextFields(
    previewDocument: Document,
    printDocument: Document,
  ): void {
    const selector =
      'textarea, select, input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"])';
    const previewFields = Array.from(
      previewDocument.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >(selector),
    );
    const printFields = Array.from(
      printDocument.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >(selector),
    );

    previewFields.forEach((previewField) => {
      const printField = this.findMatchingField(previewField, printFields);

      if (!printField) {
        return;
      }

      printField.value = previewField.value;
      printField.setAttribute('value', previewField.value);
    });
  }

  private findMatchingInput(
    previewInput: HTMLInputElement,
    printInputs: HTMLInputElement[],
  ): HTMLInputElement | undefined {
    return (
      printInputs.find(
        (printInput) =>
          printInput.type === previewInput.type &&
          printInput.id === previewInput.id &&
          printInput.name === previewInput.name &&
          printInput.value === previewInput.value,
      ) ??
      printInputs.find(
        (printInput) =>
          printInput.type === previewInput.type &&
          Boolean(previewInput.id) &&
          printInput.id === previewInput.id,
      ) ??
      printInputs.find(
        (printInput) =>
          printInput.type === previewInput.type &&
          printInput.name === previewInput.name &&
          printInput.value === previewInput.value,
      )
    );
  }

  private findMatchingField<
    T extends HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  >(previewField: T, printFields: T[]): T | undefined {
    return (
      printFields.find(
        (printField) =>
          Boolean(previewField.id) && printField.id === previewField.id,
      ) ??
      printFields.find(
        (printField) =>
          Boolean(previewField.name) && printField.name === previewField.name,
      )
    );
  }

  private async setTranslations(): Promise<void> {
    this.translateLoader
      .getTranslation(
        this.previewLanguage,
        this.TRANSLATION_KEYS,
        this.fallbackLanguage,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translation) => {
        this.translateService.setTranslation(
          this.previewLanguage,
          translation,
          true,
        );
        this.translateService.use(this.previewLanguage);
      });
  }

  private getMeta(
    questId: string,
    language?: string,
    options: { fullFetch?: boolean } = { fullFetch: true },
  ): void {
    this.previewService
      .getPreviewMetadata(questId, language)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.fallbackLanguage = undefined;
          for (const meta of response) {
            if (meta.componentType === ComponentType.CHOOSE_LANGUAGE) {
              this.fallbackLanguage = meta.defaultLanguage;
              if (options.fullFetch) {
                this.state.selectedLanguage = meta.defaultLanguage;
              }
              this.languages = meta.languages;
            } else if (meta.componentType === ComponentType.QUESTION_TITLE) {
              this.questions = meta.questions;
            } else if (meta.componentType === ComponentType.PAGES_SIMPLE) {
              this.availablePages = meta.availablePages;
            }
          }
          if (this.previewLanguage) {
            void this.setTranslations();
          }
          this.updateContent(this.previewService.createUrlParams(this.state));
        },
        error: (error) =>
          console.error('Error getting preview metadata:', error),
      });
  }
}

export default PreviewPageComponent;
